export const meta = {
  id: '10-genitive',
  order: 10,
  titleFi: 'Genetiivi',
  titleEn: 'Genitive',
  duolingoUnit: 'duo-10-genitive',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['anne', 'daenerys', 'jelmāzmo', 'jelmāzma', 'taoba', 'muña', 'ēdrugon', 'dovaogēdy', 'ours:tymvos', 'ours:ōghy', 'ours:nekiapos'],
  exampleIds: ['ex-7602d8ab90cd', 'got-s3e04-008', 'got-s4e06-002', 'got-s3e04-004', 'ex-fb2a974be75d'],
  checkFocus: { cases: ['gen'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Genitive',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Cases',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Declensions',
    'https://wiki.languageinvention.com/index.php?title=Jelmāzmo',
    'https://wiki.languageinvention.com/index.php?title=Dovaogēdy',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['cases'],
  drills: { cases: ['gen'] },
  showLines: ['got-s3e04-008', 'got-s4e06-002', 'got-s3e04-004'],
  pronunciation: { focus: 'j', words: ['Jelmāzmo', 'jelmāzma', 'jentys', 'majaqagon', 'jiōrna'], lines: ['got-s3e04-008', 'got-s4e06-002', 'got-s3e04-004'] },
  letter: {
    fi: 'Kerro kolmesta asiasta, kenelle ne kuuluvat (genetiivi).',
    en: 'Write about three things and whose they are (genitive).',
  },
  scene: 'got-s4e06-1',
}

# Genetiivi

Tähän asti valyrian substantiiveilla on ollut kaksi sijaa: nominatiivi subjektille ja akkusatiivi objektille. Nyt tulee kolmas, genetiivi. Se on suomalaiselle tähänastisista sijoista helpoin, koska käytämme samaa rakennetta joka päivä.

## Omistaja genetiiviin

Genetiivi on omistajan sija. Englannissa omistusta voi ilmaista kahdella tavalla: *the hill's top* ja *the top of the hill*. Duolingon Tips & Notes -ohjeet neuvovat ajattelemaan valyrian genetiiviä kuten ensimmäistä tapaa. Suomalaiselle se on tuttu ilman englantiakin: *mäen huippu*, *miehen hevonen*. Ensin omistaja genetiivissä, sitten omistettu.

<Compare glossed fi="miehe|n hevonen" hv="val|o anne" label="genetiivi ~ genetiivi" />

Genetiivin tunnistaa helposti. **Yksikön** genetiivin pääte on aina **-o**, joskus pitkä **-ō**, mutta ei koskaan mikään muu vokaali:

- <Hv glossed>vala</Hv> ’mies’ → <Hv glossed>valo</Hv> ’miehen’
- <Hv glossed>anne</Hv> ’hevonen’ → <Hv glossed>anno</Hv> ’hevosen’
- <Hv glossed>muña</Hv> ’äiti’ → <Hv glossed>muño</Hv> ’äidin’
- <Hv>Daenerys</Hv> → <Hv glossed>Daenero</Hv> ’Daenerysin’
- <Hv>Targārien</Hv> → <Hv glossed>Targārio</Hv> ’Targaryenin’
- pitkä -ō: <Hv glossed>ūlio</Hv> ’haukka’ → <Hv glossed>ūliō</Hv> ’haukan’ (sana tulee eläinten oppitunnilla)

Monikon genetiivi on useimmiten **-oti**: <Hv glossed>valoti</Hv> ’miesten’, <Hv glossed>muñoti</Hv> ’äitien’. Muutamassa <Term id="deklinaatio">taivutusluokassa</Term> se on **-ȳti**, esimerkiksi <Hv>Targārȳti</Hv> ja perheen oppitunnin sana <Hv glossed>lēkia</Hv> ’isoveli’ → <Hv>lēkȳti</Hv>. Tässä Duolingon ”ei koskaan muuta vokaalia” ei päde. Nämä opitaan sana kerrallaan.

<Paradigm lemma="taoba" cases="nom,acc,gen" numbers="sg,pl" />

### Omistettu on siinä sijassa, jota lause vaatii

Genetiivi koskee vain omistajaa. Omistettu sana on siinä sijassa, jota sen tehtävä lauseessa vaatii. Duolingon ohjeiden omat esimerkit näyttävät tämän: <Hv glossed>Valo anne sȳz issa</Hv> ’Miehen hevonen on hyvä’, jossa <Hv>anne</Hv> on subjektina nominatiivissa, ja <Hv glossed>Valo annī urnen</Hv> ’Näen miehen hevosen’, jossa <Hv>annī</Hv> on objektina akkusatiivissa. <Hv>Valo</Hv> pysyy molemmissa samana.

Sanakirjan esimerkkilause samasta asiasta:

<Example id="ex-7602d8ab90cd" />

<Interlinear hv="Riñ|a Dovaogēd|o muñ|e rhaen|as." gloss="tyttö-NOM tahraton-GEN äiti-ACC löytää-PRS.3SG" fi="Tyttö löytää Tahrattoman äidin." />

<Note>
Tässä suomen oma rakenne voi johtaa harhaan. Suomessa -n-pääte merkitsee genetiivin lisäksi myös kokonaisobjektia: *Näen miehen hevosen*. Lauseessa on kaksi n-päätettä: ensimmäinen on omistajan genetiivi ja toinen objektin pääte. Valyriassa nämä ovat kaksi eri sijaa: <Hv fi="miehen">valo</Hv> on genetiivi ja <Hv fi="hevosen">annī</Hv> akkusatiivi. Objekti ei siis koskaan ole genetiivissä.
</Note>

## Ensin omistaja, paitsi arvonimissä

Valyriassa järjestyksen voi kääntää: sekä <Hv glossed>valo anne</Hv> että <Hv glossed>anne valo</Hv> tarkoittavat ’miehen hevonen’. Tavallinen järjestys on kuitenkin selvästi omistaja ensin. Duolingon ohjeiden mukaan sen saa kääntää vain kahdesta syystä: kun lauseeseen kasautuu liikaa määritteitä tai kun kyse on kunnioittavasta arvonimestä.

Kuuluisin esimerkki on Daenerysin arvonimi. *Daenerys Stormborn* eli Daenerys Myrskysyntyinen on valyriaksi <Hv glossed>Daenerys Jelmāzmo</Hv>. <Hv glossed>Jelmāzmo</Hv> on sanan <Hv glossed>jelmāzma</Hv> ’myrsky, raju tuuli’ genetiivi, joten sanatarkasti nimi on ’Daenerys myrskyn’. Arvonimenä <Hv l="jelmāzma">Jelmāzmo</Hv> on sanakirjassa myös omana hakusananaan.

<Pronounce l="jelmāzmo" word="Jelmāzmo" />

<Line id="got-s3e04-008" />

Samassa repliikissä ovat molemmat järjestykset. <Hv fi="Daenerys Myrskysyntyinen">Daenerys Jelmāzmo</Hv> on arvonimi, joten genetiivi tulee nimen perään. <Hv glossed>Targārio Lentrot</Hv> taas on tavallisessa järjestyksessä, genetiivi edellä: ’Targaryenin suvusta’.

Missandein esittelyssä arvonimiä on peräkkäin monta. Viimeinen niistä, <Hv glossed>Muña Zaldrīzoti</Hv> ’Lohikäärmeiden äiti’, on käänteisessä järjestyksessä, ja <Hv>zaldrīzoti</Hv> on monikon genetiivi:

<Line id="got-s4e06-002" />

## Adjektiivi taipuu oman substantiivinsa mukaan

Adjektiivi mukautuu suvussa, luvussa ja sijassa siihen substantiiviin, jota se määrittää. Duolingon ohjeet näyttävät, mitä tämä tarkoittaa genetiivissä. Lause *The good boy's mother is sleeping* on valyriaksi <Hv fi="Hyvän pojan äiti nukkuu">Sȳro taobo muña ēdrus</Hv>. Sana ’hyvä’ määrittää poikaa eikä äitiä, ja poika on lauseessa omistaja eli genetiivissä. Siksi adjektiivikin on genetiivissä: <Hv fi="hyvän pojan">sȳro taobo</Hv>. Muoto <Hv>sȳz taobo</Hv> olisi väärin. <Hv>Muña</Hv> on nominatiivissa, koska äiti on se, joka nukkuu.

Ohjeet käyttävät tähän paljon tilaa, koska englannissa adjektiivi ei taivu. Suomalaiselle asia on tuttu, sillä sanomme täsmälleen samoin *hyvän pojan äiti*: adjektiivi on genetiivissä pojan mukana. Tässä suomi auttaa täysin.

<Compare fi="hyvä|n" hv="sȳ|ro" label="genetiivin määrite: adjektiivikin genetiivissä" />

<Practice hv="Sȳro ābro anne ēdrus." fi="Hyvän naisen hevonen nukkuu." />

<Practice hv="Valo anni ēdrusi." fi="Miehen hevoset nukkuvat." />

<Practice hv="Daenero anne ēdrus." fi="Daenerysin hevonen nukkuu." />

Verbi <Hv glossed>ēdrugon</Hv> ’nukkua’ kuuluu tämän oppitunnin sanoihin. <Hv glossed>Ēdrus</Hv> on ’hän nukkuu’ ja <Hv glossed>ēdrusi</Hv> ’he nukkuvat’. Koko preesens käydään läpi <LessonLink n={14}>oppitunnilla 14</LessonLink>.

## Tahrattomat: Dovaogēdy

Englannin *Unsullied* ei taivu. Sama sana tarkoittaa yhtä tai montaa, ja omistusmuoto *Unsullied's* voi olla yksikkö tai monikko. Valyriassa sana taipuu kuten mikä tahansa substantiivi:

<Paradigm lemma="dovaogēdy" cases="nom,acc,gen" numbers="sg,pl" />

<Pronounce word="Dovaogēdy" />

Valyria siis erottaa sen, mitä englanti ei erota: <Hv>Dovaogēdo</Hv> on yhden Tahrattoman genetiivi ja <Hv>Dovaogēdoti</Hv> monen. Suomi tekee saman eron (*tahrattoman*, *tahrattomien*), joten tässä ei tarvitse opetella uutta ajattelutapaa.

Kun Daenerys puhuttelee armeijaansa, hän käyttää muotoa <Hv>Dovaogēdys</Hv>. Se on vokatiivi eli puhuttelumuoto:

<Line id="got-s3e04-004" />

Vielä yksi monikon genetiivi Tahrattomille pidetystä puheesta, <Hv glossed>muñoti</Hv> ’äitien’:

<Example id="ex-fb2a974be75d" />

<Note>
Duolingon tehtävissä on tunnettu vika, josta Peterson varoittaa. Tehtävissä, joissa vastaus kootaan sanapaloista ja joissa on monikon genetiivi (esimerkiksi *Those are the masters' horses*), oikeaa englanninkielistä vastausta ei voi koota. Vika on Duolingon järjestelmässä, eikä kurssin tekijä pysty korjaamaan sitä. Jos jäät tällaiseen tehtävään jumiin, vika ei ole sinussa.
</Note>

<Note>
**Muotojen kuuntelukortit.** Kun tunnet sanan, päivän korteissa voi tulla vastaan myös sen genetiivi kuuntelukorttina. Kuulet muodon näkemättä sitä ja sanot, mitä se tarkoittaa ja mikä muoto se on. Ääni on Petersonin, jos hän on sanonut muodon; muuten kuulet kumppanisi taivutustaulukossa tekemän äänityksen.
</Note>

## Lopuksi

Genetiivin pääte on -o, monikossa yleensä -oti. Omistaja tulee ensin, paitsi arvonimissä. Adjektiivi seuraa omaa substantiiviaan genetiiviin, kuten suomessakin. Seuraavalla oppitunnilla omistusta ilmaistaan toisella tavalla, omistussanoilla, ja vesisuvun taivutus tuo mukanaan muutaman äänteenmuutoksen.

<Vocab />

<Check />
